import {Menu} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {DVIREntity} from "@ots-frontend/ots-data";
import {useCallback, useMemo} from "react";
import {useAuth} from "../../../providers/AuthProvider/AuthProvider";
import {
  IconAutomaticGearbox,
  IconDownload,
  IconEdit,
  IconManualGearbox,
  IconTool,
  IconTrash
} from "@tabler/icons-react";
import {useDVIRAction} from "./useDVIRAction";

const ALL_PERSON_ACTIONS = [
  "EDIT",
  "MECHANIC_REVIEW",
  "DELETE",
  "DOWNLOAD",
] as const;

export type DVIRAction = typeof ALL_PERSON_ACTIONS[number];

export type DVIRActionMenuDropdownProps = {
  dvir: DVIREntity
  actions?: DVIRAction[]
  onActionClick?: (action: DVIRAction) => void
  showEmpty?: boolean
}

export const DVIRActionMenuDropdown = (props: DVIRActionMenuDropdownProps) => {
  const {
    dvir,
    actions = ALL_PERSON_ACTIONS,
    onActionClick,
    showEmpty = true
  } = props

  const {hasAccess} = useAuth()
  const {t} = useTranslation(["common"])

  const visibleActions: Set<DVIRAction> = useMemo(() => {
    const enabledActions = new Set<DVIRAction>()
    const actionsSet = actions ? new Set<DVIRAction>(actions) : new Set<DVIRAction>()

    if (hasAccess("dvir_view")) {
      if (actionsSet.has("DOWNLOAD")) {
        enabledActions.add("DOWNLOAD")
      }
    }

    if (hasAccess("dvir_edit")) {
      if (actionsSet.has("EDIT")) {
        enabledActions.add("EDIT")
      }
    }

    if (dvir.isUnresolved) {
      if (hasAccess("dvir_review")) {
        if (actionsSet.has("MECHANIC_REVIEW")) {
          enabledActions.add("MECHANIC_REVIEW")
        }
      }
    }

    if (hasAccess("dvir_delete")) {
      if (actionsSet.has("DELETE")) {
        enabledActions.add("DELETE")
      }
    }

    return enabledActions
  }, [dvir, actions, hasAccess])

  const {
    onDVIRActionClick
  } = useDVIRAction()

  const _onActionClick = useCallback((action: DVIRAction) => {
    if (onActionClick) {
      onActionClick(action)
    } else {
      onDVIRActionClick(dvir, action)
    }
  }, [onActionClick, onDVIRActionClick, dvir])

  return (
    <>
      {(showEmpty && visibleActions.size === 0) &&
        <Menu.Item>
          {t("common.action.none", "No Actions")}
        </Menu.Item>
      }
      {visibleActions.has("DOWNLOAD") &&
        <Menu.Item icon={<IconDownload size={14}/>} onClick={() => _onActionClick("DOWNLOAD")}>
          {t("common.action.download", "Download")}
        </Menu.Item>
      }
      {visibleActions.has("EDIT") &&
        <Menu.Item icon={<IconEdit size={14}/>} onClick={() => _onActionClick("EDIT")}>
          {t("common.action.edit", "Edit")}
        </Menu.Item>
      }
      {visibleActions.has("MECHANIC_REVIEW") &&
        <Menu.Item icon={<IconTool size={14}/>} onClick={() => _onActionClick("MECHANIC_REVIEW")}>
          {t("common.action.mechanicreview", "Mechanic Review")}
        </Menu.Item>
      }
      {visibleActions.has("DELETE") &&
        <>
          {visibleActions.size > 1 && <Menu.Divider/>}
          <Menu.Item
            onClick={() => _onActionClick("DELETE")}
            icon={<IconTrash size={14}/>}
            color="red">
            {t("common.action.delete", "Delete")}
          </Menu.Item>
        </>
      }
    </>
  )
}
